import { Component, EventEmitter, Injector, Input, Output, ViewChild } from '@angular/core';
import { Card, CardSymbol, Deck } from '@deckplan.io/common';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { CardSymbologyUtils } from '../card-symbology-utils';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DecksService } from '../../decks/decks.service';
import { InventoryService } from '../../inventory/inventory.service';
import { InventoryCard, Quality } from '@deckplan.io/common/inventory';
import { AuthService } from '../../auth/auth.service';
import { Router } from '@angular/router';
import { AlertLevel, AlertsService } from '../../common/alerts/alerts.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { CardAndRelation } from '@deckplan.io/common/decks/deck';

export interface CardViewDialogData {
    card: Card;
    removable?: false | { deckId: number };
    addToDeck?: boolean | { deckId: number };
    addToInventory?: boolean;
    swappable?: boolean;
    quantity?: number;
}

export enum CardActionTaken {
  // eslint-disable-next-line @typescript-eslint/naming-convention
    Added,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Removed
}

@Component({
    selector: 'app-card-view',
    templateUrl: './card-view.component.html',
    styleUrls: ['./card-view.component.scss']
})
export class CardViewComponent {

    @Input() public card: Card;
    @Input() public removable: boolean | { deckId: number } = false;

    // TODO: This passing stuff up and down the stack is really gross
    // is there another way to do this?
    @Input() public addToDeck: boolean | { deckId: number } = true;
    @Input() public addToInventory = true;
    @Input() public swappable = false;

    @Input() public quantity = 0;

    @Output() public actionTaken = new EventEmitter<CardActionTaken>();
    @Output() public requestSwap = new EventEmitter<void>();

    public transformed = false;
    public decks: Deck[] = [];

    @ViewChild('decksMenuTrigger', {static: true}) private readonly decksMenuTrigger: MatMenuTrigger;

    constructor(public readonly cardSymbologyUtils: CardSymbologyUtils,
                private readonly injector: Injector,
                private readonly decksService: DecksService,
                private readonly inventoryService: InventoryService,
                private readonly authService: AuthService,
                private readonly router: Router,
                private readonly alertsService: AlertsService,
                private readonly dialog: MatDialog) {
        const dialogData = this.injector.get<CardViewDialogData>(MAT_DIALOG_DATA, null);
        if (dialogData === null) {
            return;
        }
        if (dialogData.card) {
            this.card = dialogData.card;
        }
        if (dialogData.removable !== undefined) {
            this.removable = dialogData.removable;
        }
        if (dialogData.addToDeck !== undefined) {
            this.addToDeck = dialogData.addToDeck;
        }
        if (dialogData.addToDeck !== undefined) {
            this.addToInventory = dialogData.addToInventory;
        }
        if (dialogData.swappable !== undefined) {
            this.swappable = dialogData.swappable;
        }

        if (dialogData.quantity !== undefined) {
            this.quantity = dialogData.quantity;
        }

        this.decksService.getAll().subscribe(deckWithTotalCount => this.decks = deckWithTotalCount.decks);
    }

    private static imgTagFromSymbol(cardSymbol: CardSymbol) {
        return `<img class="mana-symbol" alt="${cardSymbol.english}" title="${cardSymbol.english}" src="${cardSymbol.svgUri}" />`;
    }

    public getCardText(face: any = null): Observable<string> {
        if (!this.card) {
            return of('');
        }

        let text = '';

        if (this.card.oracleText) {
            text = this.card.oracleText;
        }

        if (face) {
            text = face.oracle_text;
        }

        return this.parseSymbols(text);
    }

    private parseSymbols(text: string): Observable<string> {
        const pattern = /({.+?})/g;
        // @ts-ignore String.matchAll is part of es2020
        const symbols = [...text.matchAll(pattern)].map(match => match[0]);

        if (symbols.length === 0) {
            return of(text);
        }

        return this.cardSymbologyUtils.getSymbols(symbols).pipe(
            map(cardSymbols => {
                const images = cardSymbols.map(cardSymbol => CardViewComponent.imgTagFromSymbol(cardSymbol));
                for (let i = 0; i < symbols.length; i++) {
                    text = text.replace(symbols[i], images[i]);
                }
                return text;
            })
        );
    }

    public getFlavorText() {
        if (!this.card) {
            return '';
        }

        if (this.card.flavorText) {
            return this.card.flavorText;
        }

        if (this.card.cardFaces && this.card.cardFaces.length > 0 && !this.transformed) {
            return this.card.cardFaces[0].flavor_text;
        } else if (this.card.cardFaces && this.card.cardFaces.length > 1 && this.transformed) {
            return this.card.cardFaces[1].flavor_text;
        }
    }

    public getTypeLine(face: any = null) {
        if (!this.card) {
            return '';
        }

        if (face) {
            return face.type_line.split('—')[0].trim();
        } else {
            const types = this.card.type.split('//');
            return types[0].split('—')[0].trim();
        }
    }

    public addToSpecificDeck(): boolean {
        return typeof (this.addToDeck) !== 'boolean';
    }

    public addCardToDeck(deckId = '') {
        // TODO: If not logged in, send to sign up page!
        if (!this.authService.currentUserValue) {
            this.router.navigate(['home']);
            this.dialog.closeAll();
            return;
        }

        if (!this.addToSpecificDeck() && this.addToDeck && deckId === '') {
            this.decksMenuTrigger.openMenu();
            return;
        } else if (this.addToSpecificDeck()) {
            deckId = (this.addToDeck as any).deckId;
        }
        this.decksService.addCard(deckId, this.card.id)
            .subscribe(() => this.actionTaken.emit(CardActionTaken.Added), () => {
                this.alertsService.alert('Failed to add card to deck!', AlertLevel.Warn);
            });
        // ELSE WE SHOULDN'T BE HERE!
    }

    public removeCard() {
        if (typeof (this.removable) === 'boolean' && this.removable) {
            // NOTE: This should only be called in context of the passed in card being
            // an actual inventory card!
            this.inventoryService.removeInventory(this.card as InventoryCard)
                .subscribe(() => {
                    this.actionTaken.emit(CardActionTaken.Removed);
                }, () => {
                    this.alertsService.alert('Failed to remove card from your inventory!', AlertLevel.Warn);
                });
        } else if (typeof (this.removable) !== 'boolean' && this.removable.hasOwnProperty('deckId')) {
            // NOTE: This should only be called in context of the passed in card being
            // an actual Card with Relation to a deck!
            const deckCard = this.card as CardAndRelation;
            this.decksService.removeCard((this.removable as any).deckId, deckCard.relationToDeckId)
                .subscribe(() => {
                    this.actionTaken.emit(CardActionTaken.Removed);
                }, () => {
                    this.alertsService.alert('Failed to remove card from deck!', AlertLevel.Warn);
                });
        }
        this.removable = false;
    }

    public addCardToInventory() {
        // TODO: If not logged in, send to sign up page!
        if (!this.authService.currentUserValue) {
            this.router.navigate(['home']);
            this.dialog.closeAll();
            return;
        }

        if (!this.addToInventory) {
            // Ooopsie! shouldn't have called this!
            return;
        }

        const inventoryCard: InventoryCard = {...this.card, quantity: 1, inventoryId: undefined, quality: Quality.Played, isFoil: false, added: null, updated: null};
        this.inventoryService.addToInventory([inventoryCard])
            .subscribe(() => this.actionTaken.emit(CardActionTaken.Added), () => {
                this.alertsService.alert('Failed to add card to your inventory!', AlertLevel.Warn);
            });
    }

    public swapCard() {
        this.requestSwap.emit();
    }

}
